import { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { resetTaskContextOnCompletion, resetResearchContextOnCompletion } from '@/hooks/goal-planner/task-context-service';

interface TaskContextTrackingState {
  pendingTaskUpdates: Set<string>;
  isProcessingContext: boolean;
}

/**
 * Hook for tracking task changes and managing context updates
 * Prevents conflicts with AI operations through debouncing and state coordination
 */
export const useTaskContextTracking = () => {
  const [state, setState] = useState<TaskContextTrackingState>({
    pendingTaskUpdates: new Set(),
    isProcessingContext: false
  });

  /**
   * Mark a single task as having modified context
   * This is called immediately when a task is changed
   */
  const markTaskContextAsModified = useCallback(async (taskId: string, changeType: 'create' | 'edit' | 'complete') => {
    console.log(`[TaskContextTracking] Marking task ${taskId} as modified (${changeType})`);
    
    try {
      // For completions, use the existing reset functions
      if (changeType === 'complete') {
        await Promise.all([
          resetTaskContextOnCompletion(taskId),
          resetResearchContextOnCompletion(taskId)
        ]);
        console.log(`[TaskContextTracking] Reset context flags for completed task ${taskId}`);
      } else {
        // For creates and edits, just reset the flags directly
        const { error } = await supabase
          .from('planner_tasks')
          .update({ 
            suggested_task_context_sent: false,
            suggested_research_context_sent: false 
          })
          .eq('id', taskId);

        if (error) {
          console.error('[TaskContextTracking] Error marking task as modified:', error);
          throw error;
        }
        console.log(`[TaskContextTracking] Reset context flags for ${changeType}d task ${taskId}`);
      }
    } catch (error) {
      console.error(`[TaskContextTracking] Failed to mark task ${taskId} as modified:`, error);
      // Don't throw - we don't want to break the UI for context tracking failures
    }
  }, []);

  /**
   * Batch process all pending context updates
   * This is debounced to avoid rapid successive updates
   */
  // Create a debounced function using useRef and setTimeout
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const processPendingUpdates = useCallback(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set new timeout
    timeoutRef.current = setTimeout(async () => {
    const { pendingTaskUpdates, isProcessingContext } = state;
    
    if (pendingTaskUpdates.size === 0 || isProcessingContext) {
      console.log('[TaskContextTracking] Skipping process - no updates or already processing');
      return;
    }

    console.log(`[TaskContextTracking] Processing ${pendingTaskUpdates.size} pending updates`);
    
    setState(prev => ({ ...prev, isProcessingContext: true }));
    
    try {
      // Process all pending updates
      const taskIds = Array.from(pendingTaskUpdates);
      
      // Batch update all tasks at once for efficiency
      const { error } = await supabase
        .from('planner_tasks')
        .update({ 
          suggested_task_context_sent: false,
          suggested_research_context_sent: false 
        })
        .in('id', taskIds);

      if (error) {
        throw error;
      }

      console.log(`[TaskContextTracking] Successfully processed ${taskIds.length} task updates`);
      
      // Clear pending updates
      setState(prev => ({
        ...prev,
        pendingTaskUpdates: new Set(),
        isProcessingContext: false
      }));
      
    } catch (error) {
      console.error('[TaskContextTracking] Error processing updates:', error);
      setState(prev => ({ ...prev, isProcessingContext: false }));
      // Silent failure - don't break the user experience
    }
    }, 1000); // 1 second debounce
  }, [state]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  /**
   * Track a task change and queue it for context update
   * This is the main function called by components
   */
  const trackTaskChange = useCallback((taskId: string, changeType: 'create' | 'edit' | 'complete') => {
    console.log(`[TaskContextTracking] Tracking ${changeType} for task ${taskId}`);
    
    // For immediate operations (like completion), process right away
    if (changeType === 'complete') {
      markTaskContextAsModified(taskId, changeType);
    } else {
      // For creates and edits, add to pending queue
      setState(prev => ({
        ...prev,
        pendingTaskUpdates: new Set(prev.pendingTaskUpdates).add(taskId)
      }));
      
      // Trigger debounced processing
      processPendingUpdates();
    }
  }, [markTaskContextAsModified, processPendingUpdates]);

  /**
   * Check if it's safe to perform an operation
   * Prevents conflicts with context processing
   */
  const canPerformOperation = useCallback((operation: 'generate' | 'edit' | 'complete' | 'create') => {
    const { isProcessingContext } = state;
    
    // Context processing blocks AI generation operations
    if (operation === 'generate' && isProcessingContext) {
      return false;
    }
    
    // All other operations can proceed even during context processing
    return true;
  }, [state]);

  /**
   * Wait for any pending context operations to complete
   * Useful before starting AI generation
   */
  const waitForContextProcessing = useCallback(async () => {
    const maxWaitTime = 5000; // 5 seconds max wait
    const checkInterval = 100; // Check every 100ms
    let waitedTime = 0;
    
    while (state.isProcessingContext && waitedTime < maxWaitTime) {
      await new Promise(resolve => setTimeout(resolve, checkInterval));
      waitedTime += checkInterval;
    }
    
    if (waitedTime >= maxWaitTime) {
      console.warn('[TaskContextTracking] Timeout waiting for context processing');
    }
  }, [state.isProcessingContext]);

  return {
    trackTaskChange,
    isProcessingContext: state.isProcessingContext,
    pendingUpdatesCount: state.pendingTaskUpdates.size,
    canPerformOperation,
    waitForContextProcessing
  };
};